import { isNil } from 'lodash';
import React, { useMemo, useState } from 'react';
import type { Snapshot } from 'recoil';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import {
  AllocationPanelLauncherAction,
  DepthTreeView,
  type GroupChangeType,
  PrivatesAllocatorLauncher,
  SecondarySubjectLine,
  SubjectGroupEditor,
} from 'venn-components';
import {
  analysisSubjectQuery,
  analysisViewIdState,
  displayZeroFeeState,
  isReportState,
  onSubjectInputDelete,
  requestSubjects,
  type Subject,
  subjectInputGroupName,
  subjectInputGroupSubjects,
  type SubjectInputId,
  type SubjectWithOptionalFee,
  type SubjectWithOptionalFeeAndFilters,
  useCachedLoadableValue,
  type WorkspaceFeeLimits,
  workspaceFeeLimitsState,
} from 'venn-state';
import { EllipsisTooltipSpan, Icon, ItemIcon, ItemType, TitleShimmer } from 'venn-ui-kit';
import {
  type AnalysisSubject,
  analyticsService,
  getBaseFee,
  getRequestSubjectForSecondaryAnalysisSubject,
  getRequestSubjectFromAnalysisSubject,
  getStudioRequestSubjectCashNodes,
  isHistoricalPortfolio,
  Numbers,
  useHasFF,
} from 'venn-utils';
import { InputRow } from './InputRow';
import { SubjectActionsMenu } from './SubjectActionsMenu';
import type { Portfolio } from 'venn-api';
import { cn } from '@/lib/utils';

const ACTION_MENU_CLASS = 'action-menu';

const AllocatorLauncher = ({ subject }: { subject?: AnalysisSubject }) => (
  <>
    {subject && <AllocationPanelLauncherAction subject={subject} />}
    {subject?.type === 'private-portfolio' && <PrivatesAllocatorLauncher subject={subject} />}
  </>
);

interface SubjectItemProps {
  subject: SubjectWithOptionalFeeAndFilters;
  onDelete: () => void;
  /** onSwap benefits from memoization, as it can otherwise cause excessive rendering of the dropdown. */
  onSwap: (newSubject: AnalysisSubject) => void;
  onEditFees: (subjectInputId: SubjectInputId, subject: SubjectWithOptionalFee) => void;
  onEditTaxRates: (subjectInputId: SubjectInputId, subject: SubjectWithOptionalFee) => void;
  onFilter: (groupId: SubjectInputId, portfolioId: number, filteredItemIds: string[]) => void;
  readonly: boolean;
  hideAllocatorLauncher?: boolean;
  hideSubjectActions?: boolean;
  /** The subject group the subject is part of */
  groupId: SubjectInputId;
  showAdvisoryFee?: boolean;
  showFilteredItems?: boolean;
  className?: string;
}

interface SubjectGroupRowWrapperProps {
  groupId: SubjectInputId;
  readonly: boolean;
  noHoverStyles?: boolean;
  trackGroupChanged: (
    snapshot: Snapshot,
    groupId: SubjectInputId,
    groupName: string,
    newSubjects: Subject[],
    action: GroupChangeType,
  ) => void;
  children: React.ReactNode;
  className?: string;
  deletable: boolean;
  labelFor?: string;
}

export const SubjectItem = ({
  subject,
  onDelete,
  onSwap,
  onEditFees,
  onEditTaxRates,
  onFilter,
  readonly,
  hideAllocatorLauncher,
  hideSubjectActions,
  groupId,
  showAdvisoryFee,
  showFilteredItems,
  className,
}: SubjectItemProps) => {
  const isBlockAssignment = useHasFF('block_assignment_ff');
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const hasDefaultRlAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const analysisSubject = useCachedLoadableValue(analysisSubjectQuery(subject), undefined);
  const [toggleFilteredItems, setToggleFilteredItems] = useState(true);
  const workspaceFeeLimits = useCachedLoadableValue(workspaceFeeLimitsState, undefined);

  const isReport = useRecoilValue(isReportState);
  const advisoryFee = isReport ? getBaseFee(subject) : undefined;
  const displayZeroFee = useCachedLoadableValue(displayZeroFeeState, false);
  const isPrivate = !isNil(subject.privateFundId) || !isNil(subject.privatePortfolioId);
  const isInvestment = !isNil(subject.fundId);
  const isHistorical = analysisSubject?.portfolio && isHistoricalPortfolio(analysisSubject.portfolio);
  const shouldShowFee =
    showAdvisoryFee &&
    advisoryFee !== undefined &&
    (advisoryFee !== 0 || (displayZeroFee && hasDefaultRlAdvisoryFeesFF)) &&
    !isPrivate &&
    !isHistorical &&
    (!isInvestment || hasAdvisoryFeesForRlInvestmentsFF);
  const cashNodes = analysisSubject
    ? getStudioRequestSubjectCashNodes(getRequestSubjectFromAnalysisSubject(analysisSubject))
    : [];
  const numberExcluded = cashNodes.length + (Object.keys(subject.feesMapping ?? {}).length - 1);

  const EditFeesTreeLeaf = useMemo(() => {
    const feesValidation = validateFeeAgainstWorkspaceLimits({
      fee: advisoryFee,
      workspaceFeeLimits,
      hasDefaultRlAdvisoryFeesFF,
    });
    const text = feesValidation.isValid ? (
      `${Numbers.safeFormatPercentage(advisoryFee)} Fee`
    ) : (
      <span className="inline-flex items-center leading-none text-venn-error gap-[4px]">
        <Icon type="triangle-exclamation" prefix="fas" className="m-0 p-0" />
        {Numbers.safeFormatPercentage(advisoryFee)} Fee
      </span>
    );
    return (
      <button
        tabIndex={-1}
        type="button"
        className="text-venn-dark-blue hover:text-venn-primary-main italic"
        onClick={() => onEditFees(groupId, subject)}
        onKeyUp={() => onEditFees(groupId, subject)}
      >
        {text}
      </button>
    );
  }, [advisoryFee, workspaceFeeLimits, hasDefaultRlAdvisoryFeesFF, onEditFees, groupId, subject]);

  if (!analysisSubject) {
    return (
      <div className={cn([className, SUBJECT_NAME_CLASSES, isBlockAssignment ? 'h-[20px]' : 'h-[30px]'])}>
        <TitleShimmer />
      </div>
    );
  }

  const isSelectedStrategyOrInvestment = analysisSubject.id !== analysisSubject.superId;
  const onlyShowAllLabel =
    isBlockAssignment && analysisSubject.portfolio && showFilteredItems && !subject.filteredSubjectIds?.length;
  const secondary = getRequestSubjectForSecondaryAnalysisSubject(analysisSubject);
  const itemType = (() => {
    if (analysisSubject.portfolio) {
      return isHistoricalPortfolio(analysisSubject.portfolio) ? ItemType.HistoricalPortfolio : ItemType.Portfolio;
    }
    if (analysisSubject.privatePortfolio) {
      return ItemType.PrivatePortfolio;
    }
    if (analysisSubject.private) {
      return ItemType.PrivateInvestment;
    }
    return ItemType.Investment;
  })();

  const showFilteredPortfolioTree = isBlockAssignment && analysisSubject.portfolio && showFilteredItems;

  const getFilteredPortfolioTree = () => {
    if (!showFilteredPortfolioTree && !shouldShowFee) {
      return null;
    }

    const isEditFeesOnlyTree = !showFilteredPortfolioTree;

    return (
      <div className="pl-[19px] -mt-[4px]">
        <DepthTreeView<Partial<Portfolio>>
          root={analysisSubject.portfolio ?? {}}
          maxDepth={onlyShowAllLabel ? 0 : undefined}
          shouldRenderRoot={false}
          filteredItemsIds={onlyShowAllLabel ? [analysisSubject.portfolio.id.toString()] : subject.filteredSubjectIds}
          getKey={isEditFeesOnlyTree ? () => '' : (p) => String(p.id ?? '')}
          getChildren={isEditFeesOnlyTree ? () => [] : (p) => p.children as Partial<Portfolio>[] | []}
          getLabel={isEditFeesOnlyTree ? () => null : (p) => getPortfolioDisplayName(p, subject)}
          customLastLabel={shouldShowFee ? EditFeesTreeLeaf : undefined}
          isCollapsed={isEditFeesOnlyTree ? true : !toggleFilteredItems}
        />
      </div>
    );
  };

  return (
    <div className={SUBJECT_LINES_CONTAINER_CLASSES}>
      <div
        data-testid="qa-subject-item"
        className={cn([className, SUBJECT_NAME_CLASSES, isBlockAssignment ? 'h-[20px]' : 'h-[30px]'])}
      >
        <div className="flex items-end gap-x-[4px] leading-[18px]">
          <MaybeNestedIcons isNested={isSelectedStrategyOrInvestment}>
            <ItemIcon
              item={itemType}
              dataSource={analysisSubject.superItem.dataSource || analysisSubject.portfolio?.dataSource}
              isUploaded={analysisSubject.userUploaded && !analysisSubject.isCompositeBenchmark}
              investmentSource={analysisSubject.fund?.investmentSource}
              className="w-[14px]"
            />
            {isSelectedStrategyOrInvestment && (
              <>
                <Icon type="corner" />
                <ItemIcon
                  item={
                    analysisSubject.strategy?.fund
                      ? ItemType.Investment
                      : analysisSubject.strategy && isHistoricalPortfolio(analysisSubject.strategy)
                        ? ItemType.HistoricalPortfolio
                        : ItemType.Portfolio
                  }
                />
              </>
            )}
          </MaybeNestedIcons>
          {isBlockAssignment && showFilteredItems && analysisSubject.type === 'portfolio' && (
            <Icon
              type="caret-down"
              className={cn([
                'text-venn-dark-blue text-[14px] cursor-pointer mx-[1px] transition-transform duration-300 ease-in-out',
                toggleFilteredItems ? 'rotate-0' : '-rotate-90',
              ])}
              onClick={() => setToggleFilteredItems(!toggleFilteredItems)}
            />
          )}
          <EllipsisTooltipSpan
            flex
            maxWidth={hideAllocatorLauncher ? 198 : showFilteredItems ? 168 : 184}
            className="leading-[18px]"
          >
            {analysisSubject.name}
          </EllipsisTooltipSpan>
        </div>
        <div className={cn([ACTION_MENU_CLASS, 'flex items-end gap-x-[4px] leading-[1]'])}>
          {!hideAllocatorLauncher && <AllocatorLauncher subject={analysisSubject} />}
          {!hideSubjectActions && (
            <SubjectActionsMenu
              subject={subject}
              readonly={readonly}
              onSwap={onSwap}
              onDelete={onDelete}
              onEditFees={onEditFees}
              onEditTaxRates={onEditTaxRates}
              onFilter={onFilter}
              groupId={groupId}
            />
          )}
        </div>
      </div>
      {isBlockAssignment && getFilteredPortfolioTree()}
      {!isBlockAssignment && shouldShowFee ? (
        <div className="text-venn-grey-90 ml-[34px] mb-[4px]">
          {advisoryFee.toLocaleString('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          Advisory Fee {numberExcluded > 0 && `(${numberExcluded} excluded)`}
        </div>
      ) : null}
      {!isNil(secondary) && <SecondarySubjectLine subject={secondary} />}
    </div>
  );
};

export const SubjectGroupRowWrapper = ({
  groupId,
  readonly,
  children,
  trackGroupChanged,
  noHoverStyles,
  className,
  deletable,
  labelFor,
}: SubjectGroupRowWrapperProps) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [name, setName] = useRecoilState(subjectInputGroupName(groupId));
  const isReport = useRecoilValue(isReportState);
  const viewId = useRecoilValue(analysisViewIdState);

  // TODO(will, collin, hesham): IMV2 P1 confirmation modal for deletion
  const onGroupDelete = useRecoilCallback(onSubjectInputDelete);
  const onSubjectGroupDelete = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const groupSubjects = await snapshot.getPromise(subjectInputGroupSubjects(groupId));
        const studioSubjects = await snapshot.getPromise(requestSubjects(groupSubjects));
        analyticsService.inputsSubjectGroupDeleted({
          name,
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          subjects: studioSubjects.map((subject) => ({
            type: subject.subjectType,
            id: subject.id,
            name: subject.name,
          })),
          viewId,
        });

        onGroupDelete(groupId);
      },
    [groupId, onGroupDelete, name, isReport, viewId],
  );

  return (
    <div className="flex-1">
      {editorOpen && (
        <SubjectGroupEditor
          groupId={groupId}
          onClose={() => setEditorOpen(false)}
          trackGroupChanged={trackGroupChanged}
        />
      )}
      <InputRow
        name={name}
        onNameChange={setName}
        onEdit={() => setEditorOpen(true)}
        onDelete={deletable ? onSubjectGroupDelete : undefined}
        readonly={readonly}
        noHoverStyles={noHoverStyles}
        hoverForOnlyName
        testid="qa-subject-group"
        className={className}
        labelFor={labelFor}
      >
        {children}
      </InputRow>
    </div>
  );
};

const getPortfolioDisplayName = (
  portfolio: Partial<Portfolio>,
  subject: SubjectWithOptionalFeeAndFilters | undefined,
): React.ReactNode | string => {
  const isFilteredToSingleItem = subject?.filteredSubjectIds?.length === 1;
  const isSameAsPortfolio = subject?.portfolioId === portfolio.id;
  const areOtherPortfoliosInGroupFiltered = !subject?.filteredSubjectIds?.length;

  if (areOtherPortfoliosInGroupFiltered || (isFilteredToSingleItem && isSameAsPortfolio)) {
    return <AllLabel />;
  }

  return portfolio.name ?? '';
};

const SUBJECT_LINES_CONTAINER_CLASSES = cn([
  'w-full min-h-[20px]',
  '[&>:nth-child(2)_i]:-ml-[11px] [&>:nth-child(2)_i]:-mt-[4px]',
  '[&>:nth-child(2)>span:not(:first-child)]:max-w-[calc(100%-40px)]',
  '[&>:nth-child(2)>span:first-child]:-mt-[1px]',
]);

const SUBJECT_NAME_CLASSES = cn([
  'flex flex-1 items-center justify-between relative my-0 mr-[5px] ml-[15px] text-[14px]',
  '[&_.subject-menu]:left-[unset] [&_.subject-menu]:-right-[100%]',
]);

const MaybeNestedIcons = ({ isNested, children }: { isNested: boolean; children: React.ReactNode }) => (
  <span
    className={cn(
      isNested && [
        'relative min-w-[14px] -left-[4px]',
        '[&>i]:absolute [&>span]:absolute',
        '[&>i:nth-child(1)]:-top-[11px] [&>i:nth-child(1)]:-left-[2px] [&>i:nth-child(1)]:text-[7px]',
        '[&>span:nth-child(1)]:-top-[11px] [&>span:nth-child(1)]:-left-[2px] [&>span:nth-child(1)]:text-[7px]',
        '[&>i:nth-child(2)]:rotate-180 [&>i:nth-child(2)]:font-normal [&>i:nth-child(2)]:-top-[7px] [&>i:nth-child(2)]:left-[1px] [&>i:nth-child(2)]:text-[9px] [&>i:nth-child(2)]:text-venn-grey-90',
        '[&>i:nth-child(3)]:left-[4px] [&>i:nth-child(3)]:-top-[6px]',
      ],
    )}
  >
    {children}
  </span>
);

const AllLabel = () => <span className="italic">All</span>;

export const validateFeeAgainstWorkspaceLimits = ({
  fee,
  workspaceFeeLimits,
  hasDefaultRlAdvisoryFeesFF,
}: {
  fee: number | undefined;
  workspaceFeeLimits: WorkspaceFeeLimits | undefined;
  hasDefaultRlAdvisoryFeesFF: boolean;
}): { isValid: boolean } => {
  if (!hasDefaultRlAdvisoryFeesFF) {
    return { isValid: true };
  }
  if (fee === undefined || workspaceFeeLimits === undefined) {
    return { isValid: true };
  }

  const { minFee, maxFee } = workspaceFeeLimits;

  if (minFee === undefined || maxFee === undefined) {
    return { isValid: true };
  }

  const minFeeDecimal = minFee / 100;
  const maxFeeDecimal = maxFee / 100;

  if (fee < minFeeDecimal || fee > maxFeeDecimal) {
    return { isValid: false };
  }

  return { isValid: true };
};
